/****
 * Main application layout that provides the overall structure
 * for authenticated users across the application.
 *
 * Handles responsive page layout, navigation visibility,
 * sidebar expansion/collapse behavior, content rendering,
 * and footer display. It also adjusts page spacing based
 * on device type (mobile, tablet, desktop) and supports
 * standalone widget pages with a simplified layout.
 */
import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import Navbar from "@/features/Navbar/Navbar";
import { useDeviceType } from "@/hooks/useDeviceType";
import Footer from "@/Pages/Footer";

const COLLAPSED_WIDTH = 64;
const EXPANDED_WIDTH = 300;

export default function MainLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { isMobile, isTablet } = useDeviceType();
  const location = useLocation();

  const isWidgetPage = location.pathname.startsWith("/app");

  const [notifications] = useState<any[]>([]);
  const unreadTotal = notifications.filter((n) => !n.read).length;

  return (
    <div className="flex min-h-dvh flex-col">
      {!isWidgetPage && (
        <Navbar
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          notifications={notifications}
          unreadTotal={unreadTotal}
          onRestartTour={() => window.dispatchEvent(new CustomEvent("restart-tour"))}
        />
      )}

      <main
        className={`flex-1 overflow-x-hidden transition-all duration-300 bg-[var(--bg-main)] ${
          isWidgetPage ? "pt-0" : "pt-14"
        }`}
        style={
          !isWidgetPage
            ? {
                marginLeft: isMobile
                  ? 0
                  : isTablet
                    ? `${COLLAPSED_WIDTH}px` // always 64px in tablet
                    : drawerOpen
                      ? `${EXPANDED_WIDTH}px`
                      : `${COLLAPSED_WIDTH}px`,
              }
            : undefined
        }
      >
        <Outlet />
      </main>

      {!isWidgetPage && <Footer />}
    </div>
  );
}
